import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useCallSummaries } from 'hooks/api/call-summaries';
import { useBooleanSetting } from 'hooks/settings/settings';
import InfoError from 'src/components/atoms/info-error/info-error';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getDateRange } from 'src/utils/date';
import { periodLengths } from 'src/utils/charts';
import {
  useAgentFilteredCalls,
  useAgentSorting,
  useCallTypeFilteredCalls,
} from 'hooks/charts/filters';
import { SettingName } from 'src/api/generated';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { ReportPermission } from 'src/modules/shared/types/permissions.type';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import MultiSelect from 'src/modules/shared/components/atoms/inputs/multi-select';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PageHeader } from 'src/modules/shared/components/molecules/page-header';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';
import { reportsSearchSchema } from 'src/modules/reports/schemas/reports-search.schema';
import { type SectionSidebarMenuItem } from 'src/modules/shared/components/molecules/section-menu';
import { Route as CallsReportsRoute } from 'src/routes/calls.reports';
import { Route as CallsIndexRoute } from 'src/routes/calls.index';
import BasicReports from './basic-reports';
import AdvancedReports from './advanced-reports';
import { ReportsSidebar, type ReportsTabKey } from './reports-sidebar';
import {
  BasicReportsSkeleton,
  ReportsFiltersSkeleton,
  ReportsViewSkeleton,
} from './reports-skeletons';
import type { CallType } from 'src/types/call-summaries';

ChartJS.register(...registerables, ChartDataLabels);

const initialDateRange = getDateRange('monthly', periodLengths.monthly);

const REPORT_FILTER_ROW_CLASS_NAME =
  'flex w-full min-w-0 flex-wrap items-center gap-3';
const REPORT_SELECT_MEDIUM_WIDTH_CLASS_NAME = 'w-[220px] max-w-full flex-none';
const REPORT_SELECT_WIDE_WIDTH_CLASS_NAME = 'w-[280px] max-w-full flex-none';

export const ReportsView: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { tab: requestedTab = 'basic' } = useSearch({
    from: CallsReportsRoute.id,
    select: (search) => ({
      tab: search.tab,
    }),
  });

  const { setting: advancedReportsEnabled, loading: settingLoading } =
    useBooleanSetting(SettingName.ADVANCED_REPORTS);
  const { hasPermission: canReadReport, loading: permissionLoading } =
    useHasPermissions(ReportPermission.Read);
  const showAdvancedReports = canReadReport && advancedReportsEnabled;

  const availableTabs = useMemo<SectionSidebarMenuItem[]>(
    () => [
      { id: 'basic', label: t('reports.calls.tabs.basic') },
      ...(showAdvancedReports
        ? [{ id: 'advanced', label: t('reports.calls.tabs.advanced') }]
        : []),
    ],
    [showAdvancedReports, t]
  );
  const shouldShowSidebar = availableTabs.length > 1;

  const activeTab: ReportsTabKey =
    requestedTab === 'advanced' && showAdvancedReports ? 'advanced' : 'basic';
  useEffect(() => {
    if (requestedTab === activeTab) {
      return;
    }

    void navigate({
      to: CallsReportsRoute.to,
      search: (prev) => ({
        ...reportsSearchSchema.parse(prev),
        tab: activeTab,
      }),
      replace: true,
    });
  }, [activeTab, navigate, requestedTab]);

  const handleTabChange = (tab: ReportsTabKey) => {
    void navigate({
      to: CallsReportsRoute.to,
      search: (prev) => ({
        ...reportsSearchSchema.parse(prev),
        tab,
      }),
    });
  };

  if (permissionLoading || settingLoading) {
    return (
      <ReportsViewSkeleton
        activeTab={requestedTab === 'advanced' ? 'advanced' : 'basic'}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Panel className="flex h-full flex-col">
        <PageHeader
          title={t('reports.calls.pageTitle')}
          subtitle={t('reports.calls.pageDescription')}
          backLabel={t('reports.calls.backLabel')}
          onBack={() =>
            navigate({
              to: CallsIndexRoute.to,
              search: { phoneVersion: 'v1' },
            })
          }
        />

        <div className="flex min-h-0 flex-1 overflow-hidden flex-col lg:flex-row">
          {shouldShowSidebar ? (
            <ReportsSidebar
              activeTab={activeTab}
              items={availableTabs}
              onSelect={handleTabChange}
            />
          ) : null}

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-neutral-50">
            {activeTab === 'basic' ? (
              <BasicReportsTab />
            ) : (
              <div className="min-w-0 flex-1 overflow-auto p-4">
                <AdvancedReports />
              </div>
            )}
          </div>
        </div>
      </Panel>
    </div>
  );
};

const BasicReportsTab: React.FC = () => {
  const { t } = useI18n();
  const [dateRange] = useState<Date[]>([
    initialDateRange.startDate,
    initialDateRange.endDate,
  ]);
  const {
    summaries,
    loading: summariesLoading,
    error,
  } = useCallSummaries(dateRange[0].toISOString(), dateRange[1].toISOString());

  const { selectedAgents, setSelectedAgents, filteredSummariesByAgent } =
    useAgentFilteredCalls(summaries);
  const { selectedCallType, setSelectedCallType, filteredSummariesByCallType } =
    useCallTypeFilteredCalls(filteredSummariesByAgent);
  const sortedAgents = useAgentSorting(summaries);

  useEffect(() => {
    if (sortedAgents.length === 0) {
      return;
    }

    const allAgentIds = sortedAgents.map((agent) => agent.id);
    setSelectedAgents(allAgentIds);
  }, [setSelectedAgents, sortedAgents]);

  const basicAgentOptions = useMemo(
    () =>
      sortedAgents.map((agent) => ({
        value: String(agent.id),
        content: agent.name,
      })),
    [sortedAgents]
  );
  const callTypeOptions = useMemo<{ content: string; value: CallType }[]>(
    () => [
      { content: t('reports.calls.filters.allCalls'), value: 'all' },
      { content: t('reports.calls.filters.effective'), value: 'effective' },
      { content: t('reports.calls.filters.missed'), value: 'missed' },
      { content: t('reports.calls.filters.voicemail'), value: 'voicemail' },
    ],
    [t]
  );

  if (error) {
    return <InfoError error={error} />;
  }

  return (
    <>
      <PageFiltersBar>
        {summariesLoading ? (
          <ReportsFiltersSkeleton />
        ) : (
          <div className={REPORT_FILTER_ROW_CLASS_NAME}>
            <Select
              size="sm"
              containerClassName={REPORT_SELECT_MEDIUM_WIDTH_CLASS_NAME}
              value={selectedCallType}
              onChange={(event) =>
                setSelectedCallType(event.target.value as CallType)
              }
              options={callTypeOptions}
            />
            <MultiSelect
              size="sm"
              containerClassName={REPORT_SELECT_WIDE_WIDTH_CLASS_NAME}
              options={basicAgentOptions}
              value={selectedAgents.map(String)}
              onChange={(value) => setSelectedAgents(value.map(Number))}
            />
          </div>
        )}
      </PageFiltersBar>
      <div className="min-w-0 flex-1 overflow-auto p-4">
        {summariesLoading ? (
          <BasicReportsSkeleton />
        ) : (
          <BasicReports summaries={filteredSummariesByCallType} />
        )}
      </div>
    </>
  );
};
